import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@talentmesh/ui/components/dialog';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FieldError, FormProvider } from 'react-hook-form';
import { RefineSearchCriteriaFormValues, useSearchCriteria } from '../../../api/useSearchCriteria';
import { useUpdateSearchParametersCriteria } from '../../../api/useUpdateSearchParametersCriteria';
import { useIdentifiedListContext } from '../utils/IdentifiedListContext';
import RankingCriteriaForm from './RankingCriteriaTab/RankingCriteriaForm';
import RefineSearchHeader from './RefineSearchHeader';
import {
    requiredRankingCriteriaError,
    useRefineSearchCriteriaForm,
    useRefineSearchCriteriaFormContext,
} from './useRefineSearchCriteriaForm';
import { useRecruitmentId } from '@/Hooks';
import UIStrings from '@/Utils/UIStrings';

interface RefineCriteriaDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface RefineCriteriaDialogFormProps {
    onOpenChange: (open: boolean) => void;
}

const RefineCriteriaDialogForm = ({ onOpenChange }: RefineCriteriaDialogFormProps) => {
    const { setRefineSearchCriteria } = useIdentifiedListContext();
    const form = useRefineSearchCriteriaForm();
    const [globalValidationError, setGlobalValidationError] = useState<string | undefined>(undefined);
    const { trigger } = useUpdateSearchParametersCriteria();

    useEffect(() => {
        const validationErrors = form.formState.errors;

        if (requiredRankingCriteriaError in validationErrors) {
            const { message } = validationErrors[requiredRankingCriteriaError] as FieldError;
            setGlobalValidationError(message);
        } else {
            setGlobalValidationError(undefined);
        }
    }, [form.formState.errors]);

    const handleApplyChange = async (criteriaValues: RefineSearchCriteriaFormValues) => {
        await trigger({
            searchCriteria: criteriaValues.rankingCriteria.map((sc) => ({
                id: sc.id,
                criterion: sc.criterion,
            })),
        });

        setRefineSearchCriteria(criteriaValues);
        onOpenChange(false);
    };

    return (
        <FormProvider {...form}>
            <form
                className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden"
                onSubmit={form.handleSubmit(handleApplyChange)}
            >
                <DialogHeader className="shrink-0">
                    <RefineSearchHeader
                        title={UIStrings.RefineCriteria}
                        onCancel={() => onOpenChange(false)}
                        useFormContext={useRefineSearchCriteriaFormContext}
                    />
                </DialogHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden">
                    {globalValidationError && (
                        <div className="border-destructive bg-destructive/10 text-destructive flex shrink-0 items-center gap-3 rounded-lg border px-4 py-3 text-sm">
                            <TriangleAlert className="h-5 w-5 shrink-0" />
                            {globalValidationError}
                        </div>
                    )}
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        <RankingCriteriaForm />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const RefineCriteriaDialog = ({ open, onOpenChange }: RefineCriteriaDialogProps) => {
    const recruitmentId = useRecruitmentId();
    const { data: searchCriteriaData, isLoading: isSearchCriteriaLoading } = useSearchCriteria(recruitmentId!);
    const isFormDataLoading = isSearchCriteriaLoading || !searchCriteriaData?.criteria.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[90vh] min-h-0 max-w-5xl! flex-col gap-0 overflow-hidden p-5"
                data-cy="refineCriteriaDialog"
            >
                <DialogTitle className="sr-only">{UIStrings.RefineCriteria}</DialogTitle>
                <DialogDescription className="sr-only">
                    {UIStrings.AISearch} {UIStrings.RefineCriteria}
                </DialogDescription>
                {open &&
                    (isFormDataLoading ? (
                        <div
                            className="flex min-h-40 flex-1 items-center justify-center"
                            data-cy="refineCriteriaDialogLoading"
                        >
                            <Spinner className="text-primary size-8" />
                        </div>
                    ) : (
                        <RefineCriteriaDialogForm onOpenChange={onOpenChange} />
                    ))}
            </DialogContent>
        </Dialog>
    );
};

export default RefineCriteriaDialog;
